import { readConsentDecision } from './consentCookie';

export const VISITOR_ID_STORAGE_KEY = 'qb_visitor_id';

let cached: string | null = null;

/**
 * WEB-2146: уже ли значение записано на устройство. Нужен, чтобы не бить в
 * localStorage на каждом из десятков обращений за визит (см. persistIfConsented).
 */
let persisted = false;

/**
 * Ревизия пачки 45 (находка C1) — КЛЮЧ ОТЗЫВА.
 *
 * Отказ посетителя обязан доехать до сервера: строка согласия в базе пишется
 * только по ключу посетителя, и без ключа в базе остаётся прежнее «granted»
 * (постбэки и Purchase-пиксель продолжают уходить). Но кнопка «Отклонить» тем
 * же движением стирает идентификатор с устройства — значит к моменту
 * следующего события читать его уже негде.
 *
 * Поэтому очистка не просто забывает идентификатор, а ПЕРЕКЛАДЫВАЕТ его сюда,
 * в память страницы. Это не сбор: значение не пишется ни в одно хранилище, не
 * переживает перезагрузку и годится ровно на одно — сказать серверу, по кому
 * отзывать согласие.
 */
let revoked: string | null = null;

function generate(): string {
  try {
    if (
      typeof crypto !== 'undefined' &&
      typeof crypto.randomUUID === 'function'
    ) {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to entropy fallback
  }
  return `v-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 12)}${Math.random().toString(36).slice(2, 12)}`;
}

function readPersisted(): string | null {
  try {
    return localStorage.getItem(VISITOR_ID_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writePersisted(id: string): void {
  try {
    localStorage.setItem(VISITOR_ID_STORAGE_KEY, id);
  } catch {
    /* private mode / quota — caller already holds the id in cache */
  }
}

/**
 * WEB-2146 (остаток отказа приёмки 21.09) — ЗАПИСЬ НА УСТРОЙСТВО ТОЛЬКО ПО
 * СОГЛАСИЮ.
 *
 * Закон о cookie в Европе говорит про САМУ ЗАПИСЬ на устройство, а не про
 * отправку куда-то, и политика конфиденциальности (`i18n/dictionaries/
 * legal.ts`) перечисляет `qb_visitor_id` среди записей, которые ставятся
 * только после «Принять». До этой правки `getVisitorId()` писал его
 * безусловно, решения не читая.
 *
 * Гейт стоит НА ЗАПИСИ, а не на выдаче значения — и это осознанная разница.
 * Верни мы `null` до согласия (как предлагала одна из развилок карточки),
 * `resolveAppGuid(): string` стал бы nullable, и правка расползлась бы на
 * оплату, липкость смарт-ссылки и связку провайдера. Поэтому идентификатор
 * до решения существует, но живёт ТОЛЬКО в памяти страницы: перезагрузку он
 * не переживает, следа на устройстве не оставляет.
 *
 * Вне списка стран (решение владельца Р1 — «не менять ничего») поведение
 * прежнее по построению: обе публичные страницы ставят там авто-согласие
 * (`applyConsentDecision('granted')`) в той же загрузке, что и первое
 * обращение за идентификатором, — значение то же самое, на устройстве оно
 * оказывается на один такт позже.
 */
function persistIfConsented(id: string): void {
  // `resolveAppGuid()` зовут десятки раз за визит (ревизия WEB-1416, Ф2), а
  // публичный плеер считает каждый кадр между пальцем и отрисовкой
  // (шрам WEB-1993). Поэтому проверка решения и запись идут ОДИН раз: дальше
  // повторять нечего — значение на устройстве уже то же самое.
  if (persisted) return;
  if (readConsentDecision() !== 'granted') return;
  writePersisted(id);
  persisted = true;
}

export function getVisitorId(): string {
  if (!cached) {
    cached = readPersisted() ?? generate();
  }
  // Попытка записи повторяется, пока она не удалась: решение посетителя
  // меняется между вызовами (баннер живёт на той же странице), и без этого
  // идентификатор, заведённый в памяти ДО «Принять», на устройство не попал
  // бы никогда — а именно его обещает политика.
  persistIfConsented(cached);
  return cached;
}

/**
 * WEB-3296 — идентификатор ВМЕСТЕ С ПРИЗНАКОМ «ПРОЧИТАН С УСТРОЙСТВА».
 *
 * Сервер берёт за переход деньги «один человек на проект за месяц» и ключом
 * человека считает `v` только при этом признаке (`vp=1`) и согласии `granted`
 * (WEB-3295, `resolveBillablePersonKey`). Признак истинный, когда при согласии
 * на устройстве лежит ИМЕННО это значение — прочитано при загрузке или записано
 * и прочитано обратно на первом заходе: такое значение переживёт перезагрузку,
 * и человек останется одним переходом. Живущий только в памяти страницы
 * (решения нет, отказ, устройство не пишет) — ложный: после перезагрузки он
 * был бы новым, и каждая перезагрузка стала бы новым платным переходом.
 */
export function getVisitorIdRead(): { id: string; fromDevice: boolean } {
  const id = getVisitorId();
  return {
    id,
    fromDevice: readConsentDecision() === 'granted' && readPersisted() === id,
  };
}

/**
 * Прочитать идентификатор, НЕ заводя новый. Нужен там, где сам факт появления
 * идентификатора был бы сбором данных: гейт согласия, ключ отзыва.
 */
export function peekVisitorId(): string | null {
  return cached ?? readPersisted();
}

/**
 * Ревизия 45 (C1 + C10): очистка следа зовёт это ВМЕСТО простого забывания.
 *  - память страницы больше не отдаёт стёртый идентификатор как живой
 *    (C10: кэш переживал очистку, и следующее чтение возвращало стёртое);
 *  - но само значение остаётся ключом отзыва (C1, докблок выше).
 */
export function forgetVisitorIdForRevocation(): void {
  const current = peekVisitorId();
  if (current) revoked = current;
  cached = null;
  // WEB-2146: отметку «уже записано» снимаем вместе с самой записью — очистка
  // стирает ключ из localStorage сразу после этого вызова. Не сними её,
  // посетитель, который нажал «Отклонить», а потом передумал и нажал
  // «Принять», остался бы без идентификатора навсегда: запись считала бы себя
  // уже сделанной. Тот же класс, что C10 пачки 45 (память пережила очистку).
  persisted = false;
}

/** Ключ отзыва: чем посетитель был известен серверу до отказа. */
export function getRevokedVisitorId(): string | null {
  return revoked;
}

export function __resetVisitorIdCacheForTests(): void {
  cached = null;
  revoked = null;
  persisted = false;
}
